import React from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

const getStatusLabel = status => {
  switch (status) {
    case 'waitingForPayment':
      return 'Wartet auf Zahlung';
    case 'awaitingConfirmation':
      return 'Wartet auf Bestätigung';
    case 'confirmed':
      return 'Bestätigt';
    case 'canceled':
      return 'Storniert';
    default:
      return status || 'Unbekannt';
  }
};

function ConvertToOfflineDialog({
  open,
  onClose,
  onConfirm,
  converting = false,
  loadingDuplicates = false,
  duplicateCheckFailed = false,
  duplicateBookings = [],
  withinGraceWindow = false,
}) {
  const handleClose = () => {
    if (converting) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Zu Offline-Zahlung konvertieren?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {withinGraceWindow ? (
            <Alert severity='warning'>
              Die Online-Zahlung wurde gerade erst gestartet und könnte vom Kunden noch
              abgeschlossen werden. Normalerweise warten wir die ersten Minuten ab. Bitte nur
              fortfahren, wenn der Kunde vor Ort ist und jetzt offline (z. B. bar) bezahlt.
            </Alert>
          ) : null}

          <Typography variant='body2'>
            Die offene Online-Zahlung wird in eine Offline-Zahlung umgewandelt. Der Kunde erhält
            dabei eine Buchungsbestätigung per E-Mail.
          </Typography>

          {loadingDuplicates ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={18} />
              <Typography variant='body2'>Mögliche Doppelbuchungen werden geprüft...</Typography>
            </Box>
          ) : duplicateCheckFailed ? (
            <Alert severity='info'>
              Die Prüfung auf mögliche Doppelbuchungen konnte gerade nicht geladen werden.
            </Alert>
          ) : duplicateBookings.length > 0 ? (
            <Alert severity='warning'>
              <Stack spacing={1.5}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Achtung: Für diesen Kunden existieren weitere Buchungen im selben Zeitraum.
                </Typography>
                <Stack spacing={1}>
                  {duplicateBookings.map(item => (
                    <Box
                      key={item._id || item.bookingNumber}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {item.bookingNumber || 'Ohne Buchungsnummer'} · {getStatusLabel(item.status)}
                      </Typography>
                      <Typography variant='body2'>
                        {dayjs(item.startTime).format('DD.MM.YYYY HH:mm')} -{' '}
                        {dayjs(item.endTime).format('HH:mm')} · {item.participantCount || 0} Personen
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Alert>
          ) : (
            <Alert severity='success'>
              Für diesen Kunden wurde keine weitere Buchung im selben Zeitraum automatisch gefunden. Bitte prüfe jedoch manuell, ob der Kunde vielleicht mit leicht anderen Daten oder zu einer anderen Zeit gebucht hat.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={converting}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='warning'
          onClick={onConfirm}
          disabled={converting || loadingDuplicates}
        >
          {converting ? <CircularProgress size={18} color='inherit' /> : 'Jetzt konvertieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConvertToOfflineDialog;
